

import { useEffect, useMemo, useState } from "react";
import "../App.css";
import { emitNavRefresh } from "../navRefresh";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_NAV_API;

export default function Admin() {
  const [sessionUser, setSessionUser] = useState(null);
  const [mode, setMode] = useState("LINK"); // LINK | FOLDER
  const [levels, setLevels] = useState([{ selectedId: "", selectedName: "" }]);
  const [options, setOptions] = useState([[]]); // folder options per level
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [folderName, setFolderName] = useState("");

  const [linkName, setLinkName] = useState("");
  const [embedUrl, setEmbedUrl] = useState("");
  const [linkType, setLinkType] = useState("LOOKER");

  const [departments, setDepartments] = useState(["ALL"]);
  const [department, setDepartment] = useState("ALL");
  const [deptSearch, setDeptSearch] = useState("");
  const [sectionType, setSectionType] = useState("PROCESS");
  const [treeData, setTreeData] = useState([]);
const [editNodeId, setEditNodeId] = useState(null);
const [editValue, setEditValue] = useState("");
const [viewMode, setViewMode] = useState("CREATE"); // CREATE | EDIT
const [expanded, setExpanded] = useState({});
const [editExtra, setEditExtra] = useState({});

  const navigate = useNavigate();

const createdBy = sessionUser?.email || "system@unknown";

  // ------------------------------
  // Initial load
  // ------------------------------
useEffect(() => {
  loadChildren(0, "");
  loadDepartments();
  loadTree(); // ✅ ADD
}, []);

  useEffect(() => {
  fetchSessionUser();
}, []);
async function fetchSessionUser() {
  try {
    const res = await fetch(`${API}/auth/me`, {
      credentials: "include", // 🔥 VERY IMPORTANT (cookie)
    });

    const data = await res.json();

    if (res.ok && data.user) {
      setSessionUser(data.user);
    } else {
      console.error("No session found");
    }
  } catch (err) {
    console.error("Session fetch error", err);
  }
}
function toggleNode(id) {
  setExpanded(prev => ({
    ...prev,
    [id]: !prev[id]
  }));
}
  async function loadChildren(levelIndex, parentId) {
    try {
      const r = await fetch(
  `${API}/nav/children?parentId=${encodeURIComponent(parentId)}&q=`,
  {
    credentials: "include", // ✅ ADD THIS
  }
);
      const data = await r.json();
      setOptions((prev) => {
        const next = [...prev];
        next[levelIndex] = Array.isArray(data.items) ? data.items : [];
        return next;
      });
    } catch (e) {
      console.error("loadChildren error", e);
    }
  }

  async function loadDepartments() {
    try {
      const r = await fetch(`${API}/nav/departments`, {
  credentials: "include", // ✅ ADD THIS
});
      const data = await r.json();
      const list = Array.isArray(data.items) && data.items.length ? data.items : ["ALL"];
      // Ensure ALL is present & on top
      const uniq = Array.from(new Set(["ALL", ...list]));
      setDepartments(uniq);
      setDepartment((prev) => (uniq.includes(prev) ? prev : "ALL"));
    } catch (e) {
      console.error("Failed to load departments", e);
      setDepartments(["ALL"]);
      setDepartment("ALL");
    }
  }

  // ------------------------------
  // Path helpers
  // ------------------------------
  function addLevel() {
    setLevels((prev) => [...prev, { selectedId: "", selectedName: "" }]);
    setOptions((prev) => [...prev, []]);
  }

  function removeLastLevel() {
    if (levels.length <= 1) return;
    setLevels((prev) => prev.slice(0, -1));
    setOptions((prev) => prev.slice(0, -1));
  }

  function onSelectLevel(i, nodeId, nodeName) {
    // keep levels up to i
    const nextLevels = levels.slice(0, i + 1).map((l, idx) =>
      idx === i ? { selectedId: nodeId, selectedName: nodeName } : l
    );
    setLevels(nextLevels);

    // drop deeper options
    setOptions((prev) => prev.slice(0, i + 1));

    // prefetch next level folders (optional UX)
    // (we only fetch on focus right now)
  }

  function getPathNames() {
    return levels
      .map((l) => String(l.selectedName || "").trim())
      .filter(Boolean);
  }

async function handleEdit(nodeId, embedUrl, linkType) {
  if (!editValue.trim()) return;

  try {
    const r = await fetch(`${API}/nav/editNode`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
  node_id: nodeId,
  new_name: editValue,
  embed_url: embedUrl || undefined,
  link_type: linkType || undefined,
  updated_by: createdBy,
}),
    });

    const data = await r.json();
    setMsg(`✅ ${data.message}`);
    setEditNodeId(null);
    setEditValue("");
    loadTree();
  } catch (e) {
    setMsg(`⚠ ${e.message}`);
  }
}

async function handleDelete(nodeId, name) {
  if (!window.confirm(`Delete "${name}"?`)) return;

  try {
    const r = await fetch(`${API}/nav/deleteNode?node_id=${nodeId}`, {
      method: "DELETE",
      credentials: "include",
    });

    const data = await r.json();
    setMsg(`✅ ${data.message}`);
    loadTree(); // refresh
  } catch (e) {
    setMsg(`⚠ ${e.message}`);
  }
}

  const selectedPathText = useMemo(() => {
    const p = getPathNames();
    return p.length ? p.join("  →  ") : "Root";
  }, [levels]);

  // ------------------------------
  // Department search
  // ------------------------------
  const filteredDepartments = useMemo(() => {
    const q = String(deptSearch || "").trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) => String(d).toLowerCase().includes(q));
  }, [departments, deptSearch]);

  // Keep current department valid when filtering/searching
  useEffect(() => {
    if (!departments.includes(department)) setDepartment("ALL");
  }, [departments]); // eslint-disable-line react-hooks/exhaustive-deps

  function getAutoSectionType(path) {
  if (!path || !path.length) return "EVERYONE";

  const root = path[0]?.toUpperCase();

  if (root.includes("PROCESS")) return "PROCESS";
  if (root.includes("MIS")) return "MIS";
  if (root.includes("PC")) return "PC";
  if (root.includes("SALES")) return "SALES";
  if (root.includes("COMMON")) return "EVERYONE";

  return "EVERYONE";
}

async function loadTree() {
  try {
    const r = await fetch(`${API}/nav/tree`, {
      credentials: "include",
    });
    const data = await r.json();
    setTreeData(data.items || []);
  } catch (e) {
    console.error("Tree load error", e);
  }
}
  // ------------------------------
  // Submit
  // ------------------------------
  async function submit() {
    setMsg("");
    setLoading(true);

    try {
      const path = getPathNames();
      const autoSection = getAutoSectionType(path);

      if (mode === "FOLDER") {
        if (!folderName.trim()) {
          setMsg("Folder Name is required.");
          return;
        }

        const r = await fetch(`${API}/nav/addFolder`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // ✅ ADD
         body: JSON.stringify({
            path,
            folder_name: folderName.trim(),
            section_type: autoSection,   // ✅ ADD THIS
            created_by: createdBy,
          }),
        });

        const data = await r.json();
        if (!r.ok) throw new Error(data.details || data.error || "Failed");

        setMsg(`✅ ${data.message}`);
        emitNavRefresh();
        // navigate("/");
        setFolderName("");
        return;
      }

      // LINK mode
      if (!linkName.trim() || !embedUrl.trim()) {
        setMsg("Dashboard Name and Embed URL are required.");
        return;
      }

      const r = await fetch(`${API}/nav/addLink`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",   // ✅ ADD THIS
        body: JSON.stringify({
        path,
        link: { name: linkName.trim(), embed_url: embedUrl.trim(), link_type: linkType },
        department: department || "ALL",
        section_type: autoSection,   // ✅ ADD THIS LINE
        created_by: createdBy,
      })  ,
      });

      const data = await r.json();
      if (!r.ok) throw new Error(data.details || data.error || "Failed");

      setMsg(`✅ ${data.message}`);
      emitNavRefresh();
      // navigate("/");
      setLinkName("");
      setEmbedUrl("");
      setLinkType("LOOKER");
      setDepartment("ALL");
      setDeptSearch("");
    } catch (e) {
      setMsg(`⚠ ${String(e?.message || e)}`);
    } finally {
      setLoading(false);
    }
  }

  

  // ------------------------------
  // Small UI helpers
  // ------------------------------
  const card = {
    marginTop: 16,
    padding: 16,
    border: "1px solid #e6e8ef",
    borderRadius: 14,
    background: "#fff",
  };

  const label = { fontSize: 12, color: "#6b7280", fontWeight: 800, marginBottom: 6 };
  const input = { width: "100%", padding: 12, borderRadius: 12, border: "1px solid #e6e8ef" };
  const selectStyle = { ...input };
function renderTree(nodes) {
  if (!nodes || !nodes.length) return null;

  return (
    <ul style={{ listStyle: "none", paddingLeft: 20 }}>
      {nodes.map((node) => (
        <li key={node.node_id} style={{ marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

            {/* Node Name OR Edit Input */}
{editNodeId === node.node_id ? (
  <>
    <input
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      placeholder="Name"
      style={{ padding: 4 }}
    />

    {/* 🔥 ONLY FOR LINK */}
    {node.node_type === "LINK" && (
      <>
        <input
          placeholder="Embed URL"
          defaultValue={node.embed_url}
onChange={(e) =>
  setEditExtra(prev => ({
    ...prev,
    [node.node_id]: {
      ...prev[node.node_id],
      embed_url: e.target.value
    }
  }))
}
          style={{ padding: 4 }}
        />

        <select
          defaultValue={node.link_type || "OTHER"}
          onChange={(e) => (node._newType = e.target.value)}
        >
          <option value="LOOKER">LOOKER</option>
          <option value="SCRIPT">SCRIPT</option>
          <option value="OTHER">OTHER</option>
        </select>
      </>
    )}

    <button
      onClick={() =>
handleEdit(
  node.node_id,
  editExtra[node.node_id]?.embed_url,
  editExtra[node.node_id]?.link_type
)
      }
    >
      ✅
    </button>

    <button onClick={() => setEditNodeId(null)}>❌</button>
  </>
) : (
              <>
<span
  onClick={() => node.node_type === "FOLDER" && toggleNode(node.node_id)}
  style={{ cursor: node.node_type === "FOLDER" ? "pointer" : "default" }}
>
  {node.node_type === "FOLDER"
    ? expanded[node.node_id] ? "📂" : "📁"
    : "🔗"}{" "}
  {node.node_name}
</span>

                {/* EDIT ICON */}
                <button
                  onClick={() => {
                    setEditNodeId(node.node_id);
                    setEditValue(node.node_name);
                  }}
                  style={{ border: "none", background: "transparent", cursor: "pointer" }}
                >
                  ✏️
                </button>

                {/* DELETE ICON */}
                <button
                  onClick={() => handleDelete(node.node_id, node.node_name)}
                  style={{ border: "none", background: "transparent", cursor: "pointer" }}
                >
                  🗑
                </button>
              </>
            )}
          </div>

          {/* CHILDREN */}
{node.node_type === "FOLDER" && expanded[node.node_id] && renderTree(node.children)}
        </li>
      ))}
    </ul>
  );
}
  return (
    <div style={{ padding: 18, maxWidth: 980, margin: "0 auto" }}>
        {/* 🔥 TOGGLE BUTTONS */}
<div style={{ display: "flex", gap: 10, marginTop: 12 }}>
  <button
    className="homeBtn"
    style={{
      background: viewMode === "CREATE" ? "#26428b" : "#e5e7eb",
      color: viewMode === "CREATE" ? "#fff" : "#000",
    }}
    onClick={() => setViewMode("CREATE")}
  >
    ➕ Creation
  </button>

  <button
    className="homeBtn"
    style={{
      background: viewMode === "EDIT" ? "#26428b" : "#e5e7eb",
      color: viewMode === "EDIT" ? "#fff" : "#000",
    }}
    onClick={() => setViewMode("EDIT")}
  >
    ✏️ Edit / Delete
  </button>
</div>
      {viewMode === "CREATE" && (
          <>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>

        <div>
          <h2 style={{ margin: 0, color: "#26428b" }}>Navigation Admin</h2>
          <div className="muted" style={{ paddingLeft: 0 }}>
            Select the parent path, then add a Folder or Dashboard Link.
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="radio" checked={mode === "LINK"} onChange={() => setMode("LINK")} />
            Add Dashboard Link
          </label>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input type="radio" checked={mode === "FOLDER"} onChange={() => setMode("FOLDER")} />
            Add Folder Only
          </label>
        </div>
      </div>

      {/* Parent Path */}
      <div style={card}>
        
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Parent Path</div>
            <div className="muted" style={{ paddingLeft: 0, marginTop: 0 }}>
              Current: <b>{selectedPathText}</b>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button className="homeBtn" type="button" onClick={addLevel}>
              + Add Level
            </button>
            <button className="homeBtn" type="button" onClick={removeLastLevel}>
              − Remove Level
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
          {levels.map((lvl, i) => {
            const parentId = i === 0 ? "" : levels[i - 1].selectedId;
            const opts = options[i] || [];

            return (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "110px 1fr auto",
                  gap: 10,
                  alignItems: "center",
                }}
              >
                <div style={{ color: "#6b7280", fontWeight: 900 }}>Level {i + 1}</div>

                <select
                  value={lvl.selectedId}
                  onFocus={() => loadChildren(i, parentId)}
                  onChange={(e) => {
                    const id = e.target.value;
                    const chosen = opts.find((x) => x.node_id === id);
                    onSelectLevel(i, id, chosen?.node_name || "");

                    // hard trim deeper levels
                    if (levels.length > i + 1) {
                      setLevels((prev) => prev.slice(0, i + 1));
                      setOptions((prev) => prev.slice(0, i + 1));
                    }
                  }}
                  style={{ ...selectStyle, minWidth: 320 }}
                >
                  <option value="">-- Select Folder --</option>
                  {opts
                    .filter((x) => x.node_type === "FOLDER")
                    .map((x) => (
                      <option key={x.node_id} value={x.node_id}>
                        {x.node_name}
                      </option>
                    ))}
                </select>

                <button className="homeBtn" type="button" onClick={() => loadChildren(i, parentId)}>
                  Refresh
                </button>
              </div>
            );
          })}
        </div>

      </div>

      {/* Main Inputs */}
      <div style={card}>
        {mode === "FOLDER" ? (
          <>
            <div style={{ fontWeight: 900, marginBottom: 12 }}>Create Folder</div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
              <div>
                <div style={label}>Folder Name</div>
                <input
                  value={folderName}
                  onChange={(e) => setFolderName(e.target.value)}
                  placeholder="e.g. CDSL"
                  style={input}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 900, marginBottom: 12 }}>Create Dashboard Link</div>

            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr 1fr", gap: 12 }}>
              <div>
                <div style={label}>Dashboard Name</div>
                <input
                  value={linkName}
                  onChange={(e) => setLinkName(e.target.value)}
                  placeholder="e.g. Reco 8.45 Dashboard"
                  style={input}
                />
              </div>

              <div>
                <div style={label}>Link Type</div>
                <select value={linkType} onChange={(e) => setLinkType(e.target.value)} style={selectStyle}>
                  <option value="LOOKER">LOOKER</option>
                  <option value="SCRIPT">SCRIPT</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div>
                <div style={label}>Department (Search + Select)</div>

                <input
                  value={deptSearch}
                  onChange={(e) => setDeptSearch(e.target.value)}
                  placeholder="Search department..."
                  style={{ ...input, marginBottom: 8 }}
                />

                <select value={department} onChange={(e) => setDepartment(e.target.value)} style={selectStyle}>
                  {(filteredDepartments.length ? filteredDepartments : ["ALL"]).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>

                <div className="muted" style={{ paddingLeft: 0, marginTop: 6 }}>
                  Tip: choose <b>ALL</b> if everyone should see this link.
                </div>
              </div>


            </div>

            <div style={{ marginTop: 12 }}>
              <div style={label}>Embed URL</div>
              <input value={embedUrl} onChange={(e) => setEmbedUrl(e.target.value)} placeholder="Embed URL" style={input} />
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
        <button className="homeBtn" disabled={loading} onClick={submit}>
          {loading ? "Saving..." : "Submit"}
        </button>

        <button className="homeBtn" type="button" onClick={() => loadDepartments()} disabled={loading}>
          Refresh Departments
        </button>

        {msg && <div className={msg.startsWith("✅") ? "muted" : "error"}>{msg}</div>}
      </div>
 </>
)}

{viewMode === "EDIT" && (
  <div style={{ marginTop: 30 }}>
    <div style={{ fontWeight: 900, marginBottom: 10 }}>
      Navigation Tree (Edit / Delete)
    </div>

    <div
      style={{
        border: "1px solid #e6e8ef",
        borderRadius: 10,
        padding: 12,
        maxHeight: 700,
        overflow: "auto",
        background: "#fafafa",
      }}
    >
      {renderTree(treeData)}
    </div>
  </div>
)}


    </div>
  );
}
